import { Field, FieldProps } from 'formik';
import React, { ReactNode } from 'react';
import { InjectedIntl, injectIntl } from 'react-intl';
import InputField from '../components/InputField';
import ValueFormatters from './valueFormatters';
import { TranslationHelpers } from '../../../i18n/translationHelpers';
import { ValidatorRule } from '../validator/validatorRule.constants';
import { NicknameFieldValidator } from '../validator/nicknameFieldValidator';
import { IErrorMessage } from '../components/errorMessage/errorMessage.type';
import styled from 'styled-components';
import { InfoTooltip } from '../components/InfoTooltip';
import ValidationErrors from './validationErrors';
import ErrorMessage from '../components/errorMessage/ErrorMessage';

interface INicknameFieldProps {
    autoFocus?: boolean;
    intl: InjectedIntl;
    value?: string;
    required?: boolean;
    validatorRules?: ValidatorRule[];
    tooltipText?: boolean;
}

const NicknameFieldInputWrapper = styled.div`
    position: relative;
`;
const NicknameFieldWrapper = styled.div`
    position: relative;
`;

const NicknameTooltipWrapper = styled.div`
    & > div {
        margin-bottom: -6px;
        margin-right: -12px;
    }
`;
class NicknameField extends React.Component<INicknameFieldProps> {
    private readonly ID: string;
    private readonly LABEL_ID: string;
    private readonly MAX_LENGTH: number;
    private nicknameValidator: NicknameFieldValidator;

    constructor(props: INicknameFieldProps) {
        super(props);

        this.ID = 'nickname';
        this.LABEL_ID = 'label.nickname';
        this.MAX_LENGTH = 127;

        this.validate = this.validate.bind(this);
        this.renderInputField = this.renderInputField.bind(this);

        const { validatorRules } = props;
        this.nicknameValidator = new NicknameFieldValidator(validatorRules);
    }

    public render() {
        return <Field name={this.ID} validate={this.validate} render={this.renderInputField} />;
    }

    private validate(value: string): IErrorMessage {
        const { intl, required } = this.props;

        let errorMessage: IErrorMessage = '';

        if (!value && required) {
            return ValidationErrors.fieldRequiredMessageProps(this.LABEL_ID);
        }

        if (value) {
            const err = this.nicknameValidator.validate(value, this.LABEL_ID);
            if (err !== null) {
                errorMessage = err.message;
            }
        }

        return errorMessage;
    }

    private renderInputField({
        form: { touched, errors, setFieldValue, handleBlur },
    }: FieldProps): ReactNode {
        const { autoFocus, value, required, tooltipText } = this.props;
        const isTouched = touched && touched[this.ID];
        const errorMessage = isTouched && errors && errors[this.ID] ? errors[this.ID] : undefined;
        const onBlur = (event: React.FocusEvent<any>) => {
            setFieldValue(this.ID, ValueFormatters.trimExtraSpaces(event.target.value));
            handleBlur(event);
        };

        return (
            <NicknameFieldWrapper>
                <NicknameFieldInputWrapper>
                    <InputField
                        autoComplete="nickname"
                        labelMessage={TranslationHelpers.getMessageDescriptor(this.LABEL_ID)}
                        type="text"
                        id={this.ID}
                        name={this.ID}
                        value={value}
                        autoFocus={autoFocus}
                        required={required}
                        maxLength={this.MAX_LENGTH}
                        onBlur={onBlur}
                        hasTooltip={true}
                        error={errorMessage}
                    />
                    <NicknameTooltipWrapper>
                        {tooltipText && (
                            <InfoTooltip
                                id={this.ID}
                                tooltipText={TranslationHelpers.getMessageDescriptor(
                                    'tooltip.nickname',
                                )}
                                touchSize={[40, 40]}
                                uniqueId={this.ID}
                            />
                        )}
                    </NicknameTooltipWrapper>
                </NicknameFieldInputWrapper>
            </NicknameFieldWrapper>
        );
    }
}

export default injectIntl(NicknameField);
